import React, { useCallback, useEffect, useState, useRef } from "react";
import { Table } from "reactstrap";
import TableStyle from "./../table.module.scss";
import styles from "./../../../assets/scss/custom.module.scss";
import "./checkliststyle.scss";
import {
  formatToDollar,
  formatTypeLabel,
  sanitizeInput,
  showToast
} from "../../../utils/utils";
import {
  HTTP_STATUS_SUCCESS,
  MAX_INITIAL_FUNDS_LIMIT,
  NON_NUMERIC_REGEX,
  showToastType
} from "../../../utils/constants/appConstants";
import PropTypes from "prop-types";
import { checklist, checkListTab, ownerTab } from "../../../locales/en";
import {
  getFundingHandleUpdateURL,
  getReportingUpdateSalesChecklistCommentUrl
} from "../../../utils/constants/apiEndPoints";
import privateAxios from "../../../utils/axiosPrivate";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchUserCreditScoreFundData } from "../../../redux/clientDetailsTabSlice";
import { setUsersDataCreditLimit } from "../../../redux/userManagementSlice";

const EMPTY_SEND_DATA = [];

const ChecklistTable = ({ sendData = EMPTY_SEND_DATA, userId }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  // FV2-807: read credit limit from the same cache Funding.jsx patches on save,
  // instead of fetching and holding a second, independently-stale copy of it.
  const cachedCreditLimit = useSelector(
    (state) => state.userManagement.usersData?.userAccountMeta?.creditLimit
  );
  const initialCommentData = {
    SUMMARY: "",
    FUNDS_NEEDED_FIRST: "",
    FUNDS_NEEDED_LAST: "",
    CREDIT_LIMIT: ""
  };
  const [commentData, setCommentData] = useState(initialCommentData);

  const [creditLimit, setCreditLimit] = useState("");
  const [prevCreditLimit, setPrevCreditLimit] = useState("");
  const [creditLimitError, setCreditLimitError] = useState("");
  const FUNDS_TYPES = new Set(["FUNDS_NEEDED_FIRST", "FUNDS_NEEDED_LAST"]);

  const originalNotesRef = useRef({});

  const handleCreditLimitData = async (value) => {
    try {
      const response = await privateAxios.put(
        getFundingHandleUpdateURL(userId, value)
      );
      // FV2-807: patch the shared cache right after the PUT succeeds, before
      // the funding-summary refresh below - a failure in that refresh must not
      // leave the save looking like it never happened, since the server has
      // already accepted it.
      dispatch(
        setUsersDataCreditLimit({
          userAccountId: Number(id),
          creditLimit: Number(value)
        })
      );
      if (response?.data?.status === HTTP_STATUS_SUCCESS) {
        showToast(response?.data?.message, showToastType.success);
      }
    } catch (error) {
      showToast(
        error?.response?.data?.errors || error?.response?.data?.message,
        showToastType.error
      );
      return;
    }

    try {
      await dispatch(
        fetchUserCreditScoreFundData({ userAccountId: id })
      ).unwrap();
    } catch {
      // The credit limit save already succeeded and reported so above; a
      // failed funding-summary refresh is stale data, not a failed save.
    }
  };

  const handleInputBlur = async (event) => {
    const rawValue = event?.target?.value ?? "";
    const trimmedValue = rawValue.trim();
    if (!trimmedValue) {
      setCreditLimitError(checklist?.errors?.creditBlank);
      return;
    }
    setCreditLimitError("");
    if (trimmedValue === prevCreditLimit) return;

    await handleCreditLimitData(trimmedValue);
    setPrevCreditLimit(trimmedValue);
  };

  const updateSalesData = useCallback(
    async (type) => {
      const currentValue = commentData[type];
      const originalValue = originalNotesRef.current[type] || "";

      if (currentValue === originalValue) return;

      try {
        const response = await privateAxios.post(
          getReportingUpdateSalesChecklistCommentUrl(userId),
          {
            section: type,
            note: sanitizeInput(currentValue)
          }
        );
        if (response?.data?.status === HTTP_STATUS_SUCCESS) {
          const message = FUNDS_TYPES.has(type)
            ? checklist?.errors?.amountUpdated
            : `${formatTypeLabel(type)} ${
                response?.data?.message ||
                ownerTab?.directorsOwner?.errMsg?.item1
              }`;
          showToast(message, showToastType.success);
          originalNotesRef.current[type] = currentValue;
        }
      } catch (error) {
        showToast(error?.message, showToastType.error);
      }
    },
    [commentData, userId]
  );

  const handleInputChange = (type, value) => {
    setCommentData((prev) => ({
      ...prev,
      [type]: value
    }));
  };

  const handleCurrencyInputChange = (key, value) => {
    const numericValue = value.replace(/\D/g, "");
    setCommentData((prev) => ({
      ...prev,
      [key]: numericValue
    }));
  };

  const handleCurrencyBlur = (key) => {
    const rawValue = commentData[key];
    const amount = parseCurrencyToNumber(rawValue);

    if (!rawValue.trim()) {
      showToast(checklist?.errors?.blankAmount, showToastType.error);
      return;
    }

    if (amount > MAX_INITIAL_FUNDS_LIMIT) {
      return;
    }

    updateSalesData(key);
  };

  useEffect(() => {
    const summaryNote = sendData.find((n) => n.section === "SUMMARY");
    const fundFirstNote = sendData.find(
      (n) => n.section === "FUNDS_NEEDED_FIRST"
    );
    const fundLastNote = sendData.find(
      (n) => n.section === "FUNDS_NEEDED_LAST"
    );

    const initData = {
      SUMMARY: summaryNote?.note || "",
      FUNDS_NEEDED_FIRST: fundFirstNote?.note || "",
      FUNDS_NEEDED_LAST: fundLastNote?.note || ""
    };

    setCommentData(initData);
    originalNotesRef.current = initData;
  }, [sendData]);

  useEffect(() => {
    const limitString =
      cachedCreditLimit === undefined || cachedCreditLimit === null
        ? "0"
        : cachedCreditLimit.toString();
    setCreditLimit(limitString);
    setPrevCreditLimit(limitString);
  }, [cachedCreditLimit]);

  const parseCurrencyToNumber = (value) => {
    if (!value) return 0;
    return Number(value.replace(NON_NUMERIC_REGEX, "")) || 0;
  };

  const handleCreditLimitChange = ({ target: { value } }) => {
    const trimmed = value.trim();
    setCreditLimit(value);
    if (trimmed) {
      setCreditLimitError("");
    }
  };

  return (
    <div className="checklist-table-responsive">
      <Table
        className={`${TableStyle.commonCustomtable} checklist-table no-border`}
        responsive
      >
        <tbody>
          <tr>
            <td>{checkListTab.table.td1}</td>
            <td>
              <div className={styles.tableinpuTWrap}>
                <textarea
                  rows={2}
                  value={commentData.SUMMARY}
                  onChange={(e) => handleInputChange("SUMMARY", e.target.value)}
                  onBlur={() => updateSalesData("SUMMARY")}
                />
              </div>
            </td>
          </tr>

          <tr>
            <td>{checkListTab.table.td2}</td>
            <td>
              <div className={styles.tableinpuTWrap}>
                <input
                  type="number"
                  name="creditLimit"
                  className={`${styles.inputWidthBox} number-arrows-hidden`}
                  value={creditLimit}
                  onChange={handleCreditLimitChange}
                  onFocus={(e) => setPrevCreditLimit(e.target.value.trim())}
                  onBlur={handleInputBlur}
                  min={0}
                />
                {creditLimitError && (
                  <div className="text-danger mt-1 errortext">
                    {creditLimitError}
                  </div>
                )}
              </div>
            </td>
          </tr>

          <tr>
            <td>{checkListTab.table.td3}</td>
            <td>
              <div className="d-flex flex-wrap align-items-center gap-1 gap-md-2 fundsNeeded">
                <div className="d-flex align-items-center gapAdd">
                  <span>{checkListTab.table.td4}</span>
                  <div className={`relative ${styles.tableinpuTWrap}`}>
                    <input
                      className="enterNumber"
                      type="text"
                      placeholder="Enter number only"
                      value={formatToDollar(commentData.FUNDS_NEEDED_FIRST)}
                      onChange={(e) =>
                        handleCurrencyInputChange(
                          "FUNDS_NEEDED_FIRST",
                          e.target.value
                        )
                      }
                      onBlur={() => handleCurrencyBlur("FUNDS_NEEDED_FIRST")}
                    />
                    {parseCurrencyToNumber(commentData.FUNDS_NEEDED_FIRST) >
                      MAX_INITIAL_FUNDS_LIMIT && (
                      <div className="text-danger mt-1 errortext">
                        {checklist?.errors?.extraAmount}
                      </div>
                    )}
                  </div>
                </div>
                <div className="width8" />
                <div className="d-flex align-items-center gapAdd">
                  <span>{checkListTab.table.td5}</span>
                  <div className={`relative ${styles.tableinpuTWrap}`}>
                    <input
                      type="text"
                      className="enterNumber"
                      placeholder="Enter number only"
                      value={formatToDollar(commentData.FUNDS_NEEDED_LAST)}
                      onChange={(e) =>
                        handleCurrencyInputChange(
                          "FUNDS_NEEDED_LAST",
                          e.target.value
                        )
                      }
                      onBlur={() => handleCurrencyBlur("FUNDS_NEEDED_LAST")}
                    />
                    {parseCurrencyToNumber(commentData.FUNDS_NEEDED_LAST) >
                      MAX_INITIAL_FUNDS_LIMIT && (
                      <div className="text-danger mt-1 errortext2">
                        {checklist?.errors?.extraAmount}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

ChecklistTable.propTypes = {
  sendData: PropTypes.array,
  userId: PropTypes.any
};

export default ChecklistTable;
